import type React from "react"

import { NeatPath } from "@/models/route"

import { Box } from "@mui/material"

import type { SummaryTile } from "../../data/insights.types"
import { useGetInsights } from "../../data/use-get-insights"
import { useGetScheduleMisalignment } from "../../data/use-get-schedule-misalignment"
import { useInsightsNavigate } from "../../data/use-insights-navigate"
import { InsightsHomepage } from "../../homepage"
import type {
  ActionCardSlot,
  WatchTile,
} from "../../homepage/insights-homepage.types"
import { WindowSelector } from "../../homepage/window-selector"
import { VirtualSubmeteringHeader } from "../../virtual-submetering-header"
import type { E2SiteRow } from "../schedule-misalignment-route/schedule-misalignment.types"

/**
 * Insights index route — the homepage where every WATCH report lands.
 *
 * The Site Variance WATCH tile is sourced live from `GET /neat/insights`
 * (`useGetInsights`). On request failure or a genuinely-empty success (org
 * has no variance) the tile is simply omitted — we never fabricate data.
 *
 * The ACT hero is sourced live from the E2 ScheduleMisalignment detail
 * (`useGetScheduleMisalignment`) — one card per `sites[]` row, mapped to
 * `ActionCardSlot`. On request failure or pending state the hero is simply
 * empty (all-clear card), same never-fabricate-data rule as WATCH.
 */

const toCard = (r: E2SiteRow): ActionCardSlot => ({
  id: r.action_id,
  temporal: "historical",
  impact_dollars: r.impact_dollars,
  confidence: r.confidence,
  site_label: r.site_label,
  // Human-readable on the card — not the PascalCase identifier.
  detector_label: "Schedule Misalignment",
  detector_code: "E2",
  action_text: r.action_text,
  cta_label: "Review",
  low_confidence: r.confidence === "LOW",
})

export const InsightsIndexRoute: React.FC = () => {
  const navigate = useInsightsNavigate()
  const { data: insights } = useGetInsights()
  const {
    data: scheduleMisalignment,
    isPending: scheduleMisalignmentLoading,
    isError: scheduleMisalignmentError,
  } = useGetScheduleMisalignment()

  const summary: SummaryTile | undefined = insights?.watch.find(
    (t) => t.slug === "variance"
  )

  const watch: readonly WatchTile[] = summary
    ? [
        {
          type: "site-variance",
          title: summary.title,
          temporal: "historical",
          cta: { label: "View all variance", href: "variance" },
          payload: summary,
        },
      ]
    : []

  const hero: readonly ActionCardSlot[] = (
    scheduleMisalignment?.sites ?? []
  ).map(toCard)

  // Every ACT card's Review resolves its `action_id` against the loaded E2
  // detail and deep-links to that site's detail under the actions aggregator
  // (/neat/actions/schedule-misalignment/:siteId). Falls back to the aggregator
  // (never fabricates a site) if the id can't be resolved. The trailing
  // "Explore all" card also routes to the aggregator.
  const onReviewAction = (id: string) => {
    const site = scheduleMisalignment?.sites.find((s) => s.action_id === id)
    navigate(
      site
        ? `${NeatPath.RecommendedActions}/${NeatPath.ScheduleMisalignment}/${site.site_id}`
        : NeatPath.RecommendedActions
    )
  }
  const onExploreAll = () => {
    navigate(NeatPath.RecommendedActions)
  }

  return (
    <>
      <VirtualSubmeteringHeader />
      <Box sx={{ maxWidth: 1304, mx: "auto", px: 3, pt: 2, width: "100%" }}>
        <WindowSelector />
      </Box>
      <InsightsHomepage
        hero={hero}
        heroError={scheduleMisalignmentError}
        heroLoading={scheduleMisalignmentLoading}
        periodLabel={scheduleMisalignment?.period_label}
        onExploreAll={onExploreAll}
        onReviewAction={onReviewAction}
        watch={watch}
      />
    </>
  )
}
